import 'react-toastify/dist/ReactToastify.css';

import React from 'react';
import PrivateRoute from 'components/PrivateRoute';
import CompanySelector from 'pages/CompanySelector';
import CreateOrFindCompany from 'pages/CreateOrFindCompany';
import Login from 'pages/auth/login/Login';
import ManageCompany from 'pages/ManageCompany';
import ConfirmEmail from 'pages/auth/register/ConfirmEmail';
import ForgotPassword from 'pages/auth/forgot-password/ForgotPassword';
import ForgotPasswordSuccess from 'pages/auth/forgot-password/ForgotPasswordSuccess';
import RegisterUser from 'pages/auth/register/RegisterUser';
import RegisterUserSuccess from 'pages/auth/register/RegisterUserSuccess';
import RegisterUserError from 'pages/auth/register/RegisterUserError';
import ResetPassword from 'pages/auth/forgot-password/ResetPassword';
import ResetPasswordSuccess from 'pages/auth/forgot-password/ResetPasswordSuccess';
import ResetPasswordTokenError from 'pages/auth/forgot-password/ResetPasswordTokenError';
import EditCompany from 'pages/admin/companies/EditCompany';
import AdminDataSources from 'pages/admin/dataSources/DataSources';
import EditDataSource from 'pages/admin/dataSources/EditDataSource';
import AdminDictionaries from 'pages/admin/dictionaries/Dictionaries';
import EditEpd from 'pages/admin/epds/EditEpd';
import FortnoxIntegrationPage from 'pages/admin/fortnox/FortnoxIntegrationPage';
import FortnoxInvoicesPage from 'pages/admin/fortnox/FortnoxInvoicesPage';
import IlcdEpd from 'pages/admin/integrations/ilcdEpd';
import IlcdEpdMappingList from 'pages/admin/integrations/ilcdEpd/IlcdEpdMappingList';
import IlcdEpdMapping from 'pages/admin/integrations/ilcdEpd/ilcdEpdMapping';
import EditInvoice from 'pages/admin/invoices/EditInvoice';
import AdminInvoices from 'pages/admin/invoices/Invoices';
import EditPcr from 'pages/admin/pcrs/EditPcr';
import AdminPcrs from 'pages/admin/pcrs/Pcrs';
import AdminEPDsReport from 'pages/admin/reports/EPDs';
import AdminKpis from 'pages/admin/reports/Kpis';
import AdminVerifiers from 'pages/admin/reports/Verifiers';
import EditUser from 'pages/admin/users/EditUser';
import AdminUsers from 'pages/admin/users/Users';
import Dashboard from 'pages/dashboard/Dashboard';
import NotificationsPage from 'pages/notifications/NotificationsPage';
import WelcomeToPortal from 'pages/dashboard/WelcomeToPortal';
import Invoice from 'pages/invoicing/Invoice';
import Invoices from 'pages/invoicing/ArchivedInvoices';
import CollocationCenters from 'pages/v2/admin/collocation-centers/CollocationCenters';
import AdminLicensees from 'pages/v2/admin/licensees/Licensees';
import Collections from 'pages/v2/collections/Collections';
import EditCollection from 'pages/v2/collections/EditCollection';
import EpdCompilerVersionHistoryGuard from 'pages/v2/compiler/EpdCompilerVersionHistoryGuard';
import EpdWizardVersionHistoryGuard from 'pages/wizard/EpdWizardVersionHistoryGuard';
import EpdCommentLogPage from 'pages/v2/comment-log/EpdCommentLogPage';
import CopyEpdWizard from 'pages/v2/copy/CopyEpdWizard';
import LcaValidation from 'pages/wizard/LcaValidation';
import WizardSelector from 'pages/wizard/WizardSelector';
import { Route, Switch } from 'react-router-dom';
import EpdRoutes, { EpdLinks } from 'routes/EpdRoutes';
import UserSettings from 'pages/account/UserSettings';
import AdminSearchEpd from 'pages/admin/epds/search/AdminSearchEpd';
import ShowGroupEpd from 'pages/admin/epds/ShowGroupEpd';
import AdminSearchCompanies from 'pages/admin/companies/search/AdminSearchCompanies';
import EpdCompilerVersionCompare from 'pages/v2/compiler/EpdCompilerVersionCompare';
import EpdCompilerDocumentPreview from 'pages/v2/compiler/PDF/EpdCompilerDocumentPreview';
import EpdCompilerPdfPreview from 'pages/v2/compiler/PDF/EpdCompilerPdfPreview';
import EpdCompilerPdfPreviewAuth from 'pages/v2/compiler/PDF/EpdCompilerPdfPreviewAuth';
import AdminSearchTools from 'pages/admin/tools/search/AdminSearchTools';
import AdminEditTool from 'pages/admin/tools/edit/AdminEditTool';
import DynamicFormsGate from 'components/DynamicFormsGate';
import DynamicForm from 'pages/dynamic-forms/dynamic-form/DynamicForm';
import DynamicFormVersionLog from 'pages/dynamic-forms/version-log/DynamicFormVersionLog';
import DynamicFormCommentLogPage from 'pages/dynamic-forms/comment-log/DynamicFormCommentLogPage';
import DynamicFormVersionComparison from 'pages/dynamic-forms/version-comparison/DynamicFormVersionComparison';
import EmbeddedEpdLibrary from 'pages/embed/library/EmbeddedEpdLibrary';
import EmbeddedEpdLibraryCarousel from 'pages/embed/library-carousel/EmbeddedEpdLibraryCarousel';
import ContactVerifiersPage from 'pages/verifiers/ContactVerifiersPage';
import ForbiddenPage from 'pages/ForbiddenPage';
import NotFoundPage from 'pages/NotFoundPage';
import EpdRegistrationCertificatePreviewAuth from 'pages/v2/compiler/PDF/EpdRegistrationCertificatePreviewAuth';
import EpdRegistrationCertificatePdfPreview from 'pages/v2/compiler/PDF/EpdRegistrationCertificatePdfPreview';
import DynamicFormRegistrationCertificatePreviewAuth from 'pages/dynamic-forms/dynamic-form/pdf/DynamicFormRegistrationCertificatePreviewAuth';
import DynamicFormRegistrationCertificatePdfPreview from 'pages/dynamic-forms/dynamic-form/pdf/DynamicFormRegistrationCertificatePdfPreview';
import DynamicFormDeclarationPdf from 'pages/dynamic-forms/dynamic-form/DynamicFormDeclarationPdf';
import AdminValidationRules from 'pages/admin/system/AdminValidationRules';
import AdminLCASoftware from 'pages/admin/system/AdminLCASoftware';
import AdminNotifications from 'pages/admin/system/AdminNotifications';
import AdminFeatureFlags from 'pages/admin/system/AdminFeatureFlags';
import SystemAdminRoute from 'components/SystemAdminRoute';
import CompanyEmptyCard from 'pages/admin/companies/CompanyEmptyCard';

const Routes: React.FC = () => {
  return (
    <Switch>
      <Route exact path={EpdRoutes.welcome} component={Login} />
      <Route exact path={EpdRoutes.login} component={Login} />
      <Route exact path={EpdRoutes.account.register.user} component={RegisterUser} />
      <Route exact path={EpdRoutes.account.register.userSuccess} component={RegisterUserSuccess} />
      <Route exact path={EpdRoutes.account.register.userError} component={RegisterUserError} />
      <Route exact path={EpdRoutes.account.confirmEmail} component={ConfirmEmail} />
      <Route exact path={EpdRoutes.account.forgotPassword} component={ForgotPassword} />
      <Route exact path={EpdRoutes.account.forgotPasswordSuccess} component={ForgotPasswordSuccess} />
      <Route exact path={EpdRoutes.account.resetPassword} component={ResetPassword} />
      <Route exact path={EpdRoutes.account.resetPasswordSuccess} component={ResetPasswordSuccess} />
      <Route exact path={EpdRoutes.account.resetPasswordTokenError} component={ResetPasswordTokenError} />
      <PrivateRoute
        path={'/dynamic-forms/:docId/html-pdf-document'}
        render={(props) => <DynamicFormsGate {...props} component={DynamicFormDeclarationPdf} />}
      />
      <PrivateRoute
        path={EpdRoutes.dynamicFormVersionLog}
        render={(props) => <DynamicFormsGate {...props} component={DynamicFormVersionLog} />}
      />
      <PrivateRoute
        exact
        path={EpdRoutes.dynamicFormCommentLog}
        render={(props) => <DynamicFormsGate {...props} component={DynamicFormCommentLogPage} />}
      />
      <PrivateRoute
        path={EpdRoutes.dynamicFormVersionCompare}
        render={(props) => <DynamicFormsGate {...props} component={DynamicFormVersionComparison} />}
      />
      <PrivateRoute
        path={'/dynamic-forms/:docId'}
        render={(props) => <DynamicFormsGate {...props} component={DynamicForm} />}
      />
      <PrivateRoute exact path={EpdRoutes.account.userSettings} component={UserSettings} />
      <PrivateRoute exact path={EpdRoutes.dashboard} component={Dashboard} />
      <PrivateRoute exact path={EpdRoutes.notifications} component={NotificationsPage} />
      <PrivateRoute exact path={EpdRoutes.epdVersionHistory} component={EpdCompilerVersionHistoryGuard} />
      <PrivateRoute exact path={EpdRoutes.epdWizardVersionHistory} component={EpdWizardVersionHistoryGuard} />
      <PrivateRoute exact path={EpdRoutes.epdCommentLog} component={EpdCommentLogPage} />
      <PrivateRoute exact path={EpdRoutes.epdVersionCompare} component={EpdCompilerVersionCompare} />
      <Route exact path={EpdRoutes.epdCompilerPdfPreview} component={EpdCompilerPdfPreview} />
      <Route exact path={EpdRoutes.epdCompilerPdfPreviewAuth} component={EpdCompilerPdfPreviewAuth} />
      <Route exact path={EpdRoutes.epdCompilerDocumentPreview} component={EpdCompilerDocumentPreview} />
      <Route
        exact
        path={EpdRoutes.epdRegistrationCertificatePdfPreviewAuth}
        component={EpdRegistrationCertificatePreviewAuth}
      />
      <Route
        exact
        path={EpdRoutes.epdRegistrationCertificatePdfPreviewAuth}
        component={EpdRegistrationCertificatePreviewAuth}
      />
      <Route exact path={EpdRoutes.epdRegistrationCertificatePdfPreview} component={EpdRegistrationCertificatePdfPreview} />
      <Route
        exact
        path={EpdRoutes.dynamicFormRegistrationCertificatePdfPreviewAuth}
        component={DynamicFormRegistrationCertificatePreviewAuth}
      />
      <Route
        exact
        path={EpdRoutes.dynamicFormRegistrationCertificatePdfPreview}
        component={DynamicFormRegistrationCertificatePdfPreview}
      />
      <PrivateRoute path={EpdRoutes.epd} component={WizardSelector} />
      <PrivateRoute path={EpdLinks.manageCompany(':companyId', ':tab?')} component={ManageCompany} />
      <PrivateRoute exact path={EpdRoutes.invoice} component={Invoice} />
      <PrivateRoute exact path={EpdRoutes.invoices} component={Invoices} />
      <PrivateRoute exact path={EpdRoutes.account.register.company} component={CreateOrFindCompany} />
      <PrivateRoute exact path={EpdRoutes.admin.companies.search} component={AdminSearchCompanies} />
      <PrivateRoute exact path={EpdRoutes.admin.companies.editCompany} component={EditCompany} />
      <PrivateRoute exact path={EpdRoutes.admin.invoices.invoices} component={AdminInvoices} />
      <PrivateRoute exact path={EpdRoutes.admin.invoices.editInvoice} component={EditInvoice} />
      <PrivateRoute exact path={EpdRoutes.admin.users.users} component={AdminUsers} />
      <PrivateRoute exact path={EpdRoutes.admin.users.editUser} component={EditUser} />
      <PrivateRoute exact path={EpdRoutes.admin.epds.search} component={AdminSearchEpd} />
      <PrivateRoute exact path={EpdRoutes.admin.epds.editEpd} component={EditEpd} />
      <PrivateRoute exact path={EpdRoutes.admin.epds.showGroupEpd} component={ShowGroupEpd} />
      <PrivateRoute exact path={EpdRoutes.admin.pcrs.pcrs} component={AdminPcrs} />
      <PrivateRoute exact path={EpdRoutes.admin.pcrs.editPcr} component={EditPcr} />
      <PrivateRoute exact path={EpdRoutes.admin.tools.search} component={AdminSearchTools} />
      <PrivateRoute exact path={EpdRoutes.admin.tools.editTool} component={AdminEditTool} />
      <PrivateRoute exact path={EpdRoutes.admin.dataSources.dataSources} component={AdminDataSources} />
      <PrivateRoute exact path={EpdRoutes.admin.dataSources.editDataSource} component={EditDataSource} />
      <PrivateRoute exact path={EpdRoutes.admin.dictionaries.dictionaries} component={AdminDictionaries} />
      <PrivateRoute exact path={EpdRoutes.admin.licensees.licensees} component={AdminLicensees} />
      <PrivateRoute exact path={EpdRoutes.admin.collocationCenters} component={CollocationCenters} />
      <PrivateRoute exact path={EpdRoutes.admin.kpis.summary} component={AdminKpis} />
      <PrivateRoute exact path={EpdRoutes.admin.verifiers.summary} component={AdminVerifiers} />
      <PrivateRoute exact path={EpdRoutes.admin.epdsreport.summary} component={AdminEPDsReport} />
      <PrivateRoute exact path={EpdRoutes.selectCompany} component={CompanySelector} />
      <PrivateRoute exact path={EpdRoutes.selectCompanyEmpty} component={CompanyEmptyCard} />
      <PrivateRoute exact path={EpdRoutes.admin.integration.ilcdEpd} component={IlcdEpd} />
      <PrivateRoute exact path={EpdRoutes.admin.integration.ilcdEpdMapping} component={IlcdEpdMapping} />
      <PrivateRoute exact path={EpdRoutes.admin.integration.ilcdEpdMappingList} component={IlcdEpdMappingList} />
      <PrivateRoute exact path={EpdRoutes.admin.integration.fortnoxIntegrationPage} component={FortnoxIntegrationPage} />
      <PrivateRoute exact path={EpdRoutes.admin.integration.FortnoxInvoicesPage} component={FortnoxInvoicesPage} />
      <SystemAdminRoute exact path={EpdRoutes.admin.system.validationRules} component={AdminValidationRules} />
      <SystemAdminRoute adminAllowed exact path={EpdRoutes.admin.system.lcaSoftware} component={AdminLCASoftware} />
      <SystemAdminRoute exact path={EpdRoutes.admin.system.notifications} component={AdminNotifications} />
      <SystemAdminRoute exact path={EpdRoutes.admin.system.featureFlags} component={AdminFeatureFlags} />
      <PrivateRoute exact path={EpdRoutes.collections} component={Collections} />
      <PrivateRoute exact path={EpdRoutes.editCollection} component={EditCollection} />
      <PrivateRoute exact path={EpdRoutes.copyEpd} component={CopyEpdWizard} />
      <PrivateRoute exact path={EpdRoutes.editCopyEpd} component={CopyEpdWizard} />
      <PrivateRoute exact path={EpdRoutes.lcaValidation} component={LcaValidation} />
      <PrivateRoute exact path={EpdRoutes.resources.contactVerifier} component={ContactVerifiersPage} />
      <Route exact path={EpdRoutes.embedLibrary} component={EmbeddedEpdLibrary} />
      <Route exact path={EpdRoutes.embedLibraryCarousel} component={EmbeddedEpdLibraryCarousel} />

      <Route path={EpdRoutes.forbidden} component={ForbiddenPage} />
      <Route path="*" component={NotFoundPage} />
    </Switch>
  );
};

export default Routes;
